<template>
	<TransitionRoot :show="isOpen" appear as="template">
		<Dialog as="div" @close="close">
			<TransitionChild
				as="template"
				enter="duration-300 ease-in-out"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="duration-300 ease-out"
				leave-from="opacity-100"
				leave-to="opacity-0"
			>
				<div class="fixed inset-0 z-50 bg-gray-500 bg-opacity-60" />
			</TransitionChild>
			<TransitionChild
				as="template"
				enter="duration-300 ease-in-out"
				enter-from="translate-x-full"
				enter-to="translate-x-0"
				leave="duration-300 ease-out"
				leave-from="translate-x-0"
				leave-to="translate-x-full"
			>
				<DialogPanel
					class="w-full shadow-xl flex flex-col fixed inset-y-0 right-0 z-[2147483000] overflow-y-auto bg-white sm:max-w-[448px]"
				>
					<div class="w-full pointer-events-auto h-full">
						<div
							class="flex relative h-full w-full flex-col bg-white shadow-xl py-6 px-4 sm:p-6"
						>
							<div
								v-if="cartStore.updating"
								class="absolute inset-0 z-50 bg-gray-100 bg-opacity-60 flex items-center justify-center"
							>
								<div
									class="inline-block h-5 w-5 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
									role="status"
								></div>
							</div>
							<div class="flex flex-col h-full relative">
								<div class="flex items-start justify-between">
									<h2 id="slide-over-title" class="text-lg font-medium py-0">
										{{ $t("Shopping bag") }} ({{ itemsCount }})
									</h2>
									<div class="ml-3 flex h-7 items-center">
										<button
											type="button"
											class="-m-2 p-2 !outline-none"
											@click="isOpen = false"
										>
											<span class="sr-only">Close panel</span>
											<XMarkIcon class="w-6 h-6" />
										</button>
									</div>
								</div>

								<div
									v-if="isEmpty"
									class="flex-1 min-h-0 text-center flex flex-col justify-center"
								>
									<h4 class="mb-2 font-medium text-xl text-empty-cart-header">
										{{ $t("Your shopping bag is empty") }}
									</h4>
									<p class="mb-6 text-empty-cart-desc">
										{{
											$t(
												"Looks like you haven’t added any items to the cart yet. Start shopping to fill it in."
											)
										}}
									</p>
									<nuxt-link
										:to="localePath('/')"
										class="button-secondary px-6 py-3"
										@click="close"
										>{{ $t("Start Shopping") }}
									</nuxt-link>
								</div>

								<div
									v-else
									class="flex flex-col flex-1 min-h-0 gap-4 sm:gap-6 mt-6"
								>
									<div class="flex-1 min-h-0 overflow-y-auto -mr-6">
										<SharedProductOrders
											class="flex-1 -mt-6 pr-6"
											:is-mini-cart="true"
											:line-items="cartItems || []"
										/>
									</div>
									<div class="flex flex-col mt-auto">
										<div class="flex flex-col gap-2">
											<div
												v-if="itemsTotal?.AmountIncVat"
												class="flex justify-between"
											>
												<p>{{ $t("Subtotal") }}</p>
												<Price :price="itemsTotal.AmountIncVat" />
											</div>
											<div v-if="itemsTotal?.Vat" class="flex justify-between">
												<p>{{ $t("Whereof VAT") }}</p>
												<Price :price="itemsTotal.Vat" />
											</div>
											<p class="text-sm text-gray-super-dark">
												{{ $t("cart.shipping_at_checkout") }}
											</p>
										</div>
										<div class="flex flex-col">
											<nuxt-link
												class="mt-3 flex items-center justify-center px-5 py-3 button-black"
												:to="localePath('/checkout')"
												@click="close"
											>
												{{ $t("Go to checkout") }}
											</nuxt-link>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</DialogPanel>
			</TransitionChild>
		</Dialog>
	</TransitionRoot>
</template>

<script setup lang="ts">
import { XMarkIcon } from "@heroicons/vue/24/outline";
import {
	Dialog,
	DialogPanel,
	TransitionRoot,
	TransitionChild
} from "@headlessui/vue";
import { computed } from "vue";
import Price from "../Product/ProductAttributes/Price.vue";

const isOpen = inject<Ref<boolean>>("isSidebarOpen", ref(false));

const { helloRetailApi } = useNorcePwa();
const cartStore = useCartStore();
const { viewCart } = useGtmEvents();
const {
	public: {
		norce: { isHelloRetailTracking }
	}
} = useRuntimeConfig();

const { basket } = storeToRefs(cartStore);

const cartItems = computed(() => basket?.value?.Items);
const itemsCount = computed(() => cartStore?.itemsCount());
const orderTotal = computed(() => basket?.value?.Summary?.Total);
// Products only: `Total` keeps the freight line added in checkout (BB-174).
const itemsTotal = computed(() => basket?.value?.Summary?.Items);
const itemsAmount = computed(() => basket?.value?.Summary?.Items?.Amount);
const isEmpty = computed(() => !basket?.value?.Items?.length);

const localePath = useLocalePath();

const close = () => {
	isOpen.value = false;
};

watch(isOpen, () => {
	if (isOpen.value) {
		viewCart(cartItems.value || [], itemsAmount.value || 0);
	}
});

watch(orderTotal, async (newTotal, oldTotal) => {
	if (!isHelloRetailTracking) return;

	if (
		oldTotal === undefined ||
		newTotal?.AmountIncVat !== oldTotal?.AmountIncVat
	) {
		const email = userStore().getUser?.email || null;
		helloRetailApi.track({
			serviceType: "cart",
			trackingData: {
				total: orderTotal.value?.AmountIncVat || 0,
				productNumbers: cartItems?.value?.map((item) => item.PartNo) || [],
				email: email
			}
		});
	}
});
</script>
